import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import { isV2AiConversation } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/conversations'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import { actions, mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { useAppDispatch } from '../../../data/store'
import { setToastContent } from '../../data/ltToastAutoActions'
import { useSelectedConversation } from '../data/hooks/useSelectedConversation'
import { useUpdateConversationAttributesMutation } from '../data/messagesApi'

const useMarkMessagesUnread = () => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const [updateConversationAttributes] = useUpdateConversationAttributesMutation()

  const selectedConversation = useSelectedConversation()
  // Opening a provider pair clears the caller's unread (BaseChat), but pairs have no
  // mark-unread write yet (LCDELIVERY-8020) or legacy unread counters to drive the indicators.
  const canUpdateReadState = selectedConversation?.conversation_attributes?.type !== CONVERSATION_TYPE.PROVIDER_PROVIDER
  const isV2AiCoachConversation = isV2AiConversation(selectedConversation)
  const markMessagesUnread = useCallback(
    async (conversationId?: string) => {
      const conversationIdToUse = conversationId || selectedConversation?.conversation_id
      if (!canUpdateReadState || !conversationIdToUse) {
        return
      }
      try {
        track({ event: mixpanelEvents.BUTTON_PRESS, action: actions.MARK_MESSAGE_UNREAD })
        // The unread count is updated optimistically by updateConversationAttributes' onQueryStarted,
        // so no manual conversations refetch is needed.
        await updateConversationAttributes({
          conversationId: conversationIdToUse,
          isV2: isV2AiCoachConversation,
          unreadProviderMessagesCount: 1,
        })
        dispatch(
          setToastContent({
            text: formatMessage({
              defaultMessage: 'Done! Successfully marked message as unread',
              description: 'Success message when marking a message as unread',
            }),
            id: 'MessagesPopover-Toast-Success',
            toastType: 'success',
          }),
        )
      } catch (e) {
        console.error(`Unable to update unread message - ${e}`)
      }
    },
    [
      canUpdateReadState,
      dispatch,
      formatMessage,
      isV2AiCoachConversation,
      selectedConversation?.conversation_id,
      updateConversationAttributes,
    ],
  )
  return { markMessagesUnread, canUpdateReadState }
}

export default useMarkMessagesUnread
